import ApiService from '../api.service'
import type { AxiosError } from 'axios'
import type { Stock } from '@/types/model'

interface ErrorResponseData {
  message?: string
}

class ResponseError extends Error {
  errorCode: number
  errorMessage: string

  constructor(errorCode: number, message: string = '') {
    super(message)
    this.name = 'ResponseError'
    this.message = message
    this.errorCode = errorCode
    this.errorMessage = message
  }
}

const StockService = {
  // stock
  stock: async function (params: string) {
    try {
      return ApiService.get('/api/stock' + params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // stock create
  stockCreate: async function () {
    try {
      return ApiService.get('/api/stock/create')
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // stock data
  stockData: async function (id: number) {
    try {
      return ApiService.get('/api/stock/' + id + '/edit')
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // stock add
  stockAdd: async function (params: Stock) {
    try {
      return ApiService.post('/api/stock', params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // stock edit
  stockEdit: async function (params: Stock) {
    try {
      return ApiService.put('/api/stock/' + params.id, params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // stock validate
  stockValidate: async function (params: Stock) {
    try {
      return ApiService.put('/api/stock/validate/' + params.id, params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // stock delete
  stockDelete: async function (id: number) {
    try {
      return ApiService.delete('/api/stock/' + id)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // stock restore
  stockRestore: async function (params: Stock) {
    try {
      return ApiService.post('/api/stock/' + params.id + '/restore', params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // stock delete permanent
  stockDeletePermanent: async function (params: Stock) {
    try {
      return ApiService.post('/api/stock/' + params.id + '/delete', params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // generate paid
  stockGeneratePaid: async function (params: Stock) {
    try {
      return ApiService.put('/api/stock/generate/' + params.id + '/paid', params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },
}

// 🔍 Helper type guard
function isAxiosError<T = unknown>(error: unknown): error is AxiosError<T> {
  return (error as AxiosError<T>).isAxiosError
}

export { StockService, ResponseError }
